import React, { useMemo } from "react";
import { ReceiptViewProps } from "./Models";
import useI18n from "@/hooks/useI18n";
import { useThemeContext } from "@/contexts/ThemeContext";
import Spinner from "@/atomic/atoms/Spinner";

export const ReceiptView: React.FC<ReceiptViewProps> = ({
    isLoading,
    hashParam,
    invoice,
    errorInvoice,
    generatePDF,
}) => {
    const { t } = useI18n();
    const { theme } = useThemeContext();

    const total = useMemo(() => {
        if (!invoice) return 0;
        return invoice.debts?.reduce((sum, item) => sum + Number(item?.price?.replace(/,/g, '.') || 0), 0) || 0;
    }, [invoice]);

    if (!hashParam) {
        return <div className="flex flex-col items-center justify-center min-h-screen bg-[#f5f7fb] p-5"><p style={{color: theme.colors.error}}>{t("common:error")}: {t("pages:receipt:idNotFound")}</p></div>;
    }

    if (isLoading) {
        return <Spinner />;
    }

    if (!isLoading && (errorInvoice || !invoice || !invoice.debts || invoice.debts.length < 1)) {
        return <div className="flex flex-col items-center justify-center min-h-screen bg-[#f5f7fb] p-5"><p style={{color: theme.colors.error}}>{t("pages:receipt:notFound")}</p></div>;
    }

    return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-[#f5f7fb] p-5">
            <div className="w-full max-w-[600px] bg-white rounded-lg shadow-[0_4px_12px_rgba(0,0,0,0.1)] p-[30px] box-border">
                <div className="text-center border-b border-[#eaedf3] pb-5 mb-5">
                    <img src="/pdfimage.png" alt="Logo" className="h-10 mb-[15px]" />
                    <h2 className="text-xl text-[#1e293b] m-0">{t("pages:receipt:confirmationOfPaymentOfTicket")}</h2>
                </div>

                <div className="text-[#334155] text-sm">
                    <p><strong>{t("pages:receipt:hello")}</strong></p>
                    <p className="bg-[#f0fdf4] border border-[#bbf7d0] text-[#166534] p-3 rounded-md">
                        {t("pages:receipt:yourPaymentOf")} <span className="font-bold">R$ {total.toFixed(2).replace('.', ',')}</span> {t("pages:receipt:wasSuccessfully")}
                    </p>

                    <h3 className="text-[15px] text-[#0f172a] mt-[25px]">{t("pages:receipt:transactionData")} #{invoice?.debts[0].id}</h3>
                    
                    <div className="flex flex-col gap-3">
                        {invoice?.debts?.map((debt, index) => (
                            <div key={index} className="border border-[#e2e8f0] rounded-md p-3 bg-[#f8fafc]">
                                <div className="flex justify-between py-1"><span>{t("pages:receipt:plate")}</span><strong>{debt.plate || "N/A"}</strong></div>
                                <div className="flex justify-between py-1"><span>{t("pages:receipt:date")}</span><span>{debt.fmtDataTransacao}</span></div>
                                <div className="flex justify-between py-1"><span>{t("pages:receipt:concessionary")}</span><span>{debt.owner || "N/A"}</span></div>
                                <div className="flex justify-between py-1"><span>{t("pages:receipt:port")}</span><span>{debt.nmPraca || "N/A"}</span></div>
                                <div className="flex justify-between py-1"><span>{t("pages:receipt:toll")}</span><span>R$ {debt.originalPrice}</span></div>
                                <div className="flex justify-between py-1"><span>{t("pages:receipt:fines")}</span><span>R$ {debt.fine}</span></div>
                                <div className="flex justify-between py-1 border-t border-[#e2e8f0] mt-2 pt-2">
                                    <span>{t("pages:receipt:total")}:</span>
                                    <strong>R$ {debt.price}</strong>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="text-center mt-[30px]">
                    <button
                        className="bg-[var(--color-primary)] text-white border-none py-3 px-6 rounded-md font-bold cursor-pointer"
                        onClick={generatePDF}
                    >
                        {t("pages:receipt:downloadReceipt")}
                    </button>
                </div>
            </div>
        </div>
    );
};
